import { ActivityType } from "@/types/activity"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { formatDateString, formatDurationFromStrings, formatDate, formatToLocalTime, formatDateForActivityTable } from "../../../lib/utils/date"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Edit, Trash2, Moon, Wind, Music, Volume2, HelpCircle } from "lucide-react"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { useEffect } from "react"

interface SleepTableProps {
  activities: ActivityType[]
  onActivityClick?: (activity: ActivityType) => void
  onEditActivity?: (activity: ActivityType) => void
  onDeleteActivity?: (activity: ActivityType) => void
  timezone?: string
}

// Helper function to determine badge variant based on quality
function getQualityVariant(quality?: string | null): "default" | "outline" | "secondary" | "destructive" {
  if (!quality) return "outline";
  
  // Convert quality to lowercase for case-insensitive comparison
  const qualityLower = quality.toLowerCase();
  
  if (qualityLower.includes('good') || qualityLower.includes('excellent')) {
    return "default"; // Use default (primary color) for good quality
  } else if (qualityLower.includes('poor') || qualityLower.includes('bad')) {
    return "destructive";
  } else if (qualityLower.includes('fair') || qualityLower.includes('average')) {
    return "secondary";
  }
  
  return "outline";
}

// Component for sound icon based on sound type
function SoundIcon({ type }: { type: string }) {
  if (!type) return <HelpCircle className="h-4 w-4 text-muted-foreground" />;
  
  const soundType = type.toLowerCase();
  
  if (soundType.includes('white') || soundType.includes('noise')) {
    return <Wind className="h-4 w-4 text-sky-400" />;
  } else if (soundType.includes('lullaby') || soundType.includes('music')) {
    return <Music className="h-4 w-4 text-indigo-400" />;
  } else {
    return <Volume2 className="h-4 w-4 text-emerald-400" />;
  }
}

// Quality badge component with proper styling
function QualityBadge({ quality, name }: { quality?: string | null, name?: string | null }) {
  const variant = getQualityVariant(quality);
  
  return (
    <Badge variant={variant}>
      {name || 'Not specified'}
    </Badge>
  );
}

export function SleepTable({ 
  activities, 
  onActivityClick = () => {}, 
  onEditActivity = () => {},
  onDeleteActivity = () => {},
  timezone
}: SleepTableProps) {
  // Debug: Log the activities when they change
  useEffect(() => {
    if (activities && activities.length > 0) {
      console.log('🏆 [SleepTable] Received activities:', activities.length);
      // Log the first activity to inspect its structure
      console.log('🏆 [SleepTable] First activity sample:', activities[0]);
      
      // Check specific fields that might be missing
      const fieldCheck = activities.map(a => ({
        id: a.id,
        type: a.type,
        onsetMethodName: a.onsetMethodName,
        environmentName: a.environmentName,
        qualityName: a.qualityName,
        soundName: a.soundName
      }));
      console.log('🏆 [SleepTable] Field check on activities:', fieldCheck);
    } else {
      console.log('🏆 [SleepTable] No activities received');
    }
  }, [activities]);

  if (!activities || activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
        <div className="mb-4 bg-indigo-900/30 text-indigo-200 p-5 rounded-full">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-14 w-14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            <circle cx="8" cy="9" r="0.5" />
            <circle cx="12" cy="8" r="0.3" />
            <circle cx="16" cy="11" r="0.4" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-indigo-200 mb-2">No Sleep Records Yet</h3>
        <p className="text-muted-foreground text-sm max-w-xs">
          Track your child's sleep patterns to help establish healthy sleep routines.
        </p>
      </div>
    );
  }

  const handleRowClick = (activity: ActivityType) => {
    onActivityClick(activity);
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Start Date</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Quality</TableHead>
            <TableHead>Onset Method</TableHead>
            <TableHead>Environment</TableHead>
            <TableHead>Sound</TableHead>
            <TableHead>Notes</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {activities.map((activity) => {
            // Debug: Log each activity as it's being rendered
            console.log(`🏆 [SleepTable] Rendering activity ${activity.id}:`, {
              onsetMethodName: activity.onsetMethodName,
              environmentName: activity.environmentName
            });
            
            return (
              <TableRow 
                key={activity.id} 
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleRowClick(activity)}
              >
                <TableCell>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>{formatDateForActivityTable(activity.startTime)}</span>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Started: {formatDate(new Date(activity.startTime))}</p>
                        <p>Ended: {formatDate(new Date(activity.endTime))}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
                
                <TableCell>
                  <span className="font-medium">
                    {formatDurationFromStrings(activity.startTime, activity.endTime)}
                  </span>
                </TableCell>
                
                <TableCell>
                  <QualityBadge quality={activity.quality} name={activity.qualityName} />
                </TableCell>
                
                <TableCell>
                  {activity.onsetMethodName || 'Not specified'}
                </TableCell>
                
                <TableCell>
                  {activity.environmentName || 'Not specified'}
                </TableCell>
                
                <TableCell>
                  {activity.soundName ? (
                    <div className="flex items-center gap-1">
                      <SoundIcon type={activity.soundName} />
                      <span>
                        {activity.soundName} ({activity.soundLevelName || 'unknown level'})
                      </span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">None</span>
                  )}
                </TableCell>
                
                <TableCell>
                  {activity.notes && activity.notes.length > 20 ? (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help">{activity.notes.substring(0, 20)}...</span>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">
                          {activity.notes}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <span>{activity.notes || '-'}</span>
                  )}
                </TableCell>
                
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditActivity(activity);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteActivity(activity);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  )
}
